import Vue from "vue"
import _ from "lodash"
import { COACH_SEARCH_FILTERS } from "@/store/coaching/coaching.constants"
import { WIDGET_STATES, WIDGET_MAPPED_COACH_STATUSES } from "@/store/coaching/widget/widget.constants"
import { GAME_KEYS } from "@/store/game/game.constants"
import { mapStatus, getCoachList } from "@/store/coaching/widget/widget.utils"
import {
  filterCoachesByGame,
  filterCoachesByRegions,
  filterCoachesByRanks,
  filterCoachesByLanguages,
  filterCoachesByPlatforms,
  filterCoachesBySpecialties,
  filterCoachesByPrices,
  filterCoachesBySearchQuery,
} from "@/store/coaching/widget/coach-filters/coach-filters.utils"
import { getCoachUser, getCoachByUsername, getCoachReviews } from "@/api/coaching"
import CoachFilters from "./coach-filters/coach-filters.module"
import ServiceCheckout from "./service-checkout/service-checkout.module"
import { setStorageCoachingWidgetStorageCollapsed } from "@/utils/coachingWidget.storage"

const state = () => ({
  collapsed: true,
  drawerHighlighted: false,
  widgetState: null,
  previousWidgetState: null,
  isModalFocused: false,
  // Additional state data
  selectedGame: null,
  // Service booking
  redirectCoachId: null,
  redirectCoachUsername: null,
  selectedCoach: null,
  coachProfileLoading: false,
  userInSession: false,
  currentRequest: {
    id: null,
    coach: {},
    type: null,
    games: 1,
    game: null,
    communicationMethod: null,
    created: null,
  },
  // Local cache of coach data
  coachAddedPosition: 0,
  coachDict: {},
  coachReviewsCache: {},
})

const mutations = {
  SET_COLLAPSED: (state, collapsed) => {
    state.collapsed = collapsed
  },
  SET_DRAWER_HIGHLIGHTED: (state, highlighted) => {
    state.drawerHighlighted = highlighted
  },
  SET_WIDGET_STATE: (state, widgetState) => {
    state.previousWidgetState = state.widgetState
    state.widgetState = widgetState
  },
  SET_SELECTED_GAME: (state, game) => {
    state.selectedGame = game
  },
  SET_IS_MODAL_FOCUSED: (state, isFocused) => {
    state.isModalFocused = isFocused
  },
  SET_REDIRECT_COACH_ID: (state, id) => {
    state.redirectCoachId = id
  },
  SET_REDIRECT_COACH_USERNAME: (state, username) => {
    state.redirectCoachUsername = username
  },
  SET_SELECTED_COACH: (state, coach) => {
    state.selectedCoach = coach
  },
  SET_COACH_PROFILE_LOADING: (state, isLoading) => {
    state.coachProfileLoading = isLoading
  },
  SET_USER_IN_SESSION: (state, inSession) => {
    state.userInSession = inSession
  },
  SET_CURRENT_REQUEST_ID: (state, id) => {
    Vue.set(state.currentRequest, "id", id)
  },
  SET_CURRENT_REQUEST_COACH: (state, coach) => {
    Vue.set(state.currentRequest, "coach", coach)
  },
  SET_CURRENT_REQUEST_TYPE: (state, type) => {
    Vue.set(state.currentRequest, "type", type)
  },
  SET_CURRENT_REQUEST_GAMES: (state, games) => {
    Vue.set(state.currentRequest, "games", games)
  },
  SET_CURRENT_REQUEST_GAME: (state, game) => {
    Vue.set(state.currentRequest, "game", game)
  },
  SET_CURRENT_REQUEST_COMMUNICATION_METHOD: (state, communicationMethod) => {
    Vue.set(state.currentRequest, "communicationMethod", communicationMethod)
  },
  SET_CURRENT_REQUEST_CREATED: (state, created) => {
    Vue.set(state.currentRequest, "created", created)
  },
  INCREMENT_COACH_ADDED_POSITION: (state) => {
    ++state.coachAddedPosition
  },
  SAVE_COACH: (state, coachData) => {
    const coachId = coachData?.coachId
    if (coachId) {
      Vue.set(state.coachDict, coachId, coachData)
    }
  },
  SAVE_COACH_REVIEWS: (state, { coachUserId, reviews }) => {
    Vue.set(state.coachReviewsCache, coachUserId, reviews)
  },
}

const actions = {
  toggleDrawerCollapse ({ state, commit }, collapse) {
    const newCollapsedState = _.isNil(collapse) ? !state.collapsed : !!collapse
    commit("SET_COLLAPSED", newCollapsedState)
    // set local state values needed
    setStorageCoachingWidgetStorageCollapsed(newCollapsedState)
  },
  setDrawerHighlighted ({ commit }, highlighted) {
    commit("SET_DRAWER_HIGHLIGHTED", highlighted)
  },
  setWidgetState ({ commit }, widgetState) {
    commit("SET_WIDGET_STATE", widgetState)
  },
  setSelectedGame ({ commit }, game) {
    commit("SET_SELECTED_GAME", game)
  },
  toggleModalFocus ({ state, commit }, focused) {
    commit("SET_IS_MODAL_FOCUSED", _.isNil(focused) ? !state.isModalFocused : !!focused)
  },
  setRedirectCoachId ({ commit }, id) {
    commit("SET_REDIRECT_COACH_ID", id)
  },
  setRedirectCoachUsername ({ commit }, username) {
    commit("SET_REDIRECT_COACH_USERNAME", username)
  },
  setSelectedCoach ({ commit }, coach) {
    commit("SET_SELECTED_COACH", coach)
  },
  setUserInSession ({ commit }, inSession) {
    commit("SET_USER_IN_SESSION", inSession)
  },
  setCoachProfileLoading ({ commit }, isLoading) {
    commit("SET_COACH_PROFILE_LOADING", isLoading)
  },
  setCurrentRequestId ({ commit }, id) {
    commit("SET_CURRENT_REQUEST_ID", id)
  },
  setCurrentRequestCoach ({ commit }, coach) {
    commit("SET_CURRENT_REQUEST_COACH", coach)
  },
  setCurrentRequestType ({ commit }, type) {
    commit("SET_CURRENT_REQUEST_TYPE", type)
  },
  setCurrentRequestGames ({ commit }, games) {
    commit("SET_CURRENT_REQUEST_GAMES", games)
  },
  setCurrentRequestGame ({ commit }, game) {
    commit("SET_CURRENT_REQUEST_GAME", game)
  },
  setCurrentRequestCommunicationMethod ({ commit }, communicationMethod) {
    commit("SET_CURRENT_REQUEST_COMMUNICATION_METHOD", communicationMethod)
  },
  setCurrentRequestCreated ({ commit }, created) {
    commit("SET_CURRENT_REQUEST_CREATED", created)
  },
  updateCoaches ({ state, getters, commit, rootGetters }, coachList) {
    coachList.forEach(coachUserData => {
      const coach = _.clone(getters.mapCoachData(coachUserData))
      const coachId = coach?.id
      const prevCoach = state.coachDict[coachId] || null
      const prevCoachStatus = _.get(prevCoach, "status")
      const currentCoachStatus = coach?.status
      // Group statuses into availability set and check state transition from that set
      const statusSet = [WIDGET_MAPPED_COACH_STATUSES.ONLINE, WIDGET_MAPPED_COACH_STATUSES.BUSY]
      const coachHasStatusUpdate =
        (statusSet.includes(prevCoachStatus) && !statusSet.includes(currentCoachStatus)) ||
        (!statusSet.includes(prevCoachStatus) && statusSet.includes(currentCoachStatus))

      // Update coach data and bump coach order if status update occurred
      if (coachHasStatusUpdate) {
        commit("INCREMENT_COACH_ADDED_POSITION")
      }

      const coachData = Object.assign(
        {},
        prevCoach || {},
        _.omitBy(coach, _.isNil),
        {
          added: coachHasStatusUpdate ? state.coachAddedPosition : coach.added,
        }
      )
      commit("SAVE_COACH", coachData)

      // if coach is favorite - check notification cooldown, show accordingly, and update cooldown if necessary
      const favoriteCoaches = rootGetters["Coaching/favoriteCoaches"] || []
      const isCoachFavorited = favoriteCoaches.map(coach => coach?.id).includes(coachId)
      if (coachHasStatusUpdate && isCoachFavorited && currentCoachStatus === WIDGET_MAPPED_COACH_STATUSES.ONLINE) {
      //   this.addFavoriteCoachNotification(coach)
      }
    })
  },
  async loadCoach ({ dispatch }, coachUserId) {
    const coachResponse = await getCoachUser(coachUserId)
    const coachUserData = coachResponse?.result || null
    if (!coachUserData) {
      return
    }

    await dispatch("Coaching/saveCoachUser", coachUserData, { root: true })
    await dispatch("updateCoaches", [coachUserData])
  },
  async loadAllFeaturedCoaches ({ dispatch }) {
    const coachList = await getCoachList([COACH_SEARCH_FILTERS.FEATURED])
    await dispatch("updateCoaches", coachList)
  },
  async loadAllAvailableCoaches ({ dispatch }) {
    const coachList = await getCoachList([COACH_SEARCH_FILTERS.ONLINE_OR_HAS_OPEN_SCHEDULE_SLOTS])
    await dispatch("updateCoaches", coachList)
  },
  async loadFirstPageOfOfflineCoaches ({ dispatch }) {
    const coachList = await getCoachList([COACH_SEARCH_FILTERS.OFFLINE], 1)
    await dispatch("updateCoaches", coachList)
  },
  async loadCoachReviews ({ state, commit, rootGetters }, coachUserId) {
    if (coachUserId in state.coachReviewsCache) {
      return
    }

    const instaProAccessToken = rootGetters["User/Coaching/getInstaProAccessToken"]
    const data = await getCoachReviews(coachUserId, instaProAccessToken, 1)
    commit("SAVE_COACH_REVIEWS", {
      coachUserId: coachUserId,
      reviews: data.result || [],
    })
  },
  async checkRedirectCoach ({ state, commit, dispatch, getters, rootGetters }) {
    try {
      let coachResponse = null
      if (state.redirectCoachId) {
        coachResponse = await getCoachUser(state.redirectCoachId)
      } else if (state.redirectCoachUsername) {
        const instaProAccessToken =
          rootGetters["User/Coaching/getInstaProAccessToken"]
        coachResponse = await getCoachByUsername(state.redirectCoachUsername, instaProAccessToken)
      } else {
        return
      }

      if (coachResponse) {
        // UPDATE COACH LIST HERE WITH RESPONSE OF THIS COACH
        const coachUserData = coachResponse?.result || null
        if (!coachUserData) {
          return
        }

        await dispatch("updateCoaches", [coachUserData])

        commit("SET_SELECTED_COACH", getters.mapCoachData(coachUserData))
        // If coach doesnt support current selected game, switch to one of those.
        const hasSelectedGame = Object.keys(state.getSelectedCoach?.games || {})
          .find(gameKey => gameKey === state.selectedGame)
        if (!hasSelectedGame) {
          const coachDefaultGame = Object.keys(state.getSelectedCoach?.games || {})[0]
          if (coachDefaultGame) {
            commit("SET_SELECTED_GAME", coachDefaultGame)
          }
        }
        commit("SET_WIDGET_STATE", WIDGET_STATES.SELECTING)
        commit("SET_REDIRECT_COACH_ID", null)
        commit("SET_REDIRECT_COACH_USERNAME", null)
      }
    } catch (error) {
      console.error("Error on resolving coach redirect: ", error)
    }
  },
}

const getters = {
  mapCoachData: (state, getters, rootState, rootGetters) => coachData => {
    // Create games object for coach, key will be game name
    const gamesObj = {}
    const coachGames = coachData.games || []
    const coachUserData = coachData.user || {}

    for (let i = 0; i < coachGames.length; i++) {
      const coachGameData = coachGames[i]
      const gameKey = coachGameData?.game?.name || null
      if (gameKey !== null) {
        const gameName = rootGetters["Game/gameNameByKey"](gameKey)
        const services = coachGameData.services
        // Session pricing
        const sessionRates = _
          .chain(coachGameData.session_rates || {})
          .pickBy(x => x)
          .value()

        // Only include coach game if session rates is not empty
        if (_.size(sessionRates) > 0) {
          gamesObj[gameKey] = {
            id: coachGameData.game_id,
            coachGameId: coachGameData.id,
            name: gameName,
            platforms: coachGameData.platforms,
            regions: coachGameData.regions,
            specialties: coachGameData.specializations,
            specializationGroupItems: coachGameData.specialization_items || {},
            specializationItemFocuses: coachGameData.specialization_item_focuses || [],
            username: coachGameData.username,
            rank: coachGameData.achievement,
            price: sessionRates || {},
            defaultGameType: _.first(Object.keys(sessionRates)),
            services: services,
            is_featured: coachGameData.is_featured,
          }
        }
      }
    }

    const status = mapStatus(coachData.status, coachData.status_state)
    const reviews = coachData?.simple_data?.requests?.all || []

    const languages = coachData?.additional?.languages || []
    const mappedLanguages = languages.map(language => {
      return language[0].toUpperCase() + language.slice(1)
    })

    const primaryLanguages = coachData?.additional?.primary_languages || []
    const mappedPrimaryLanguages = primaryLanguages.map(primaryLanguage => {
      return primaryLanguage[0].toUpperCase() + primaryLanguage.slice(1)
    })

    const secondaryLanguages = coachData?.additional?.secondary_languages || []
    let mappedSecondaryLanguages = secondaryLanguages.map(secondaryLanguage => {
      return secondaryLanguage[0].toUpperCase() + secondaryLanguage.slice(1)
    })

    const selectedGame = rootGetters["Coaching/coachingGame"]
    const platforms = gamesObj[selectedGame] ? gamesObj[selectedGame].platforms : []

    return {
      pusherChannel: coachData?.broadcast_channel || "no_channel",
      name: coachUserData.nickname,
      id: coachUserData.id,
      pgId: coachUserData.pg_id,
      coachId: coachData.id,
      games: !_.isEmpty(gamesObj) ? gamesObj : null,
      videoType: coachData.additional?.introduction_video?.type,
      videoCode: coachData.additional?.introduction_video?.id,
      status: status,
      avatar: coachData.additional?.photo,
      rating: coachData.simple_data?.requests?.avg,
      ratings: coachData.simple_data?.requests?.total,
      studentCount: coachData.student_count,
      reviews: reviews,
      typicalSession: coachData.coaching_session,
      bio: coachData.introduction,
      username: coachData.username,
      discord: coachData.additional?.social_media?.discord,
      skype: coachData.additional?.social_media?.skype,
      languages: mappedLanguages,
      primary_languages: mappedPrimaryLanguages,
      secondary_languages: mappedSecondaryLanguages,
      platforms,
      hasOpenScheduleSlots: !!coachData.earliest_availability,
    }
  },
  getSpecializationItemData: (state, getters, rootState, rootGetters) => (gameKey, itemName) => {
    switch (gameKey) {
      case GAME_KEYS.LEAGUE_OF_LEGENDS:
        // Load champions
        const championGetter = rootGetters["Game/LeagueOfLegends/getChampionByName"]
        const championData = championGetter(itemName)
        return championData
          ? {
            thumbnailImage: championData?.thumbnail_image,
            tooltip: championData?.name,
          }
          : null
      case GAME_KEYS.SSBU:
        // Load characters
        const characterGetter = rootGetters["Game/SSBU/getCharacterByName"]
        const characterData = characterGetter(itemName)
        return characterData
          ? {
            thumbnailImage: characterData?.thumbnail_image,
            tooltip: characterData?.name,
          }
          : null
    }
  },
  userLoggedIn: (state, getters, rootState, rootGetters) => {
    return !!(rootGetters["User/Coaching/getInstaProAccessToken"] && rootGetters["User/getUser"])
  },
  collapsed: state => state.collapsed,
  drawerHighlighted: state => state.drawerHighlighted,
  widgetState: state => state.widgetState,
  previousWidgetState: state => state.previousWidgetState,
  isModalFocused: state => state.isModalFocused,
  selectedGame: state => state.selectedGame,
  redirectCoachId: state => state.redirectCoachId,
  redirectCoachUsername: state => state.redirectCoachUsername,
  selectedCoach: state => state.selectedCoach,
  coachProfileLoading: state => state.coachProfileLoading,
  userInSession: state => state.userInSession,
  currentRequest: state => state.currentRequest,
  coachDict: state => state.coachDict,
  coachByUserId: state => userId => Object.values(state.coachDict).find(coach => coach.id === userId) || null,
  coachReviewsCache: state => state.coachReviewsCache,
  coaches: state => {
    return Object.values(state.coachDict).sort((a, b) => {
      if (a.added > b.added) {
        return 1
      } else if (a.added < b.added) {
        return -1
      }
      return 0
    })
  },
  filteredCoaches: (state, getters, rootState, rootGetters) => {
    const selectedRegions = Object.keys(rootGetters["Coaching/CoachWidget/CoachFilters/selectedFilterRegions"] || {})
    const selectedRanks = Object.keys(rootGetters["Coaching/CoachWidget/CoachFilters/selectedFilterRanks"] || {})
    const selectedLanguages = Object.keys(rootGetters["Coaching/CoachWidget/CoachFilters/selectedFilterLanguages"] || {})
    const selectedPlatforms = Object.keys(rootGetters["Coaching/CoachWidget/CoachFilters/selectedFilterPlatforms"] || {})
    const selectedSpecialties = Object.keys(rootGetters["Coaching/CoachWidget/CoachFilters/selectedFilterSpecialties"] || {})
    const searchQueryValue = rootGetters["Coaching/CoachWidget/CoachFilters/searchQueryValue"] || ""
    const filter = _.flow([
      filterCoachesByGame(state.selectedGame),
      filterCoachesByRegions(state.selectedGame, selectedRegions),
      filterCoachesByRanks(state.selectedGame, selectedRanks),
      filterCoachesByLanguages(selectedLanguages),
      filterCoachesByPlatforms(selectedPlatforms),
      filterCoachesBySpecialties(state.selectedGame, selectedSpecialties),
      filterCoachesByPrices(state.selectedGame, []),
      filterCoachesBySearchQuery(state.selectedGame, searchQueryValue),
    ])
    return filter(getters.coaches)
  },
  favoriteCoaches: (state, getters, rootState, rootGetters) => {
    const favoriteCoaches = rootGetters["Coaching/favoriteCoaches"] || []
    const applyGameFilter = filterCoachesByGame(state.selectedGame)
    const favoriteCoachesByGame = applyGameFilter(favoriteCoaches.map(coach => {
      return getters.mapCoachData(coach)
    }))

    return favoriteCoachesByGame
  },
  featuredCoaches: (state, getters) => {
    return getters.filteredCoaches.filter(coachData => {
      const coachGameData = coachData.games[state.selectedGame]
      const isNotOfflineOrHasOpenSchedule = (coachData.status !== WIDGET_MAPPED_COACH_STATUSES.OFFLINE || coachData.hasOpenScheduleSlots)

      return coachGameData.is_featured && isNotOfflineOrHasOpenSchedule
    })
  },
  availableCoaches: (state, getters) => {
    return getters.filteredCoaches.filter(coachData => {
      return [WIDGET_MAPPED_COACH_STATUSES.ONLINE, WIDGET_MAPPED_COACH_STATUSES.BUSY].includes(coachData.status)
    })
  },
  inSessionCoaches: (state, getters) => {
    return getters.filteredCoaches.filter(coachData => {
      return coachData.status === WIDGET_MAPPED_COACH_STATUSES.IN_SESSION
    })
  },
  onlineCoaches: (state, getters) => {
    return getters.availableCoaches.concat(getters.inSessionCoaches)
  },
  offlineCoaches: (state, getters) => {
    return getters.filteredCoaches.filter(coachData => {
      return coachData.status === WIDGET_MAPPED_COACH_STATUSES.OFFLINE
    })
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules: {
    CoachFilters,
    ServiceCheckout,
  },
}



// WEBPACK FOOTER //
// ./src/store/coaching/widget/widget.module.js